import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import api from '../api';

function getDefaultHomeForRole(role) {
  if (role === 'club_member') return '/clubmember/welcome';
  if (role === 'admin' || role === 'college_admin') return '/login/admin/home';
  return '/student/welcome';
}

export default function RequireAuth({
  children,
  redirectTo = '/login',
  allowedRoles,
  forbiddenTo,
}) {
  const location = useLocation();
  const [status, setStatus] = useState('checking'); // checking | authed | unauthed | forbidden
  const [userRole, setUserRole] = useState('');
  const lastCheckIdRef = useRef(0);

  const checkAuth = async () => {
    const checkId = ++lastCheckIdRef.current;
    setStatus('checking');

    try {
      // If this succeeds, we consider the session valid.
      const me = await api.get('users/me/');
      const role = (me?.role || '').toString();

      if (lastCheckIdRef.current !== checkId) return;

      setUserRole(role);
      if (Array.isArray(allowedRoles) && allowedRoles.length > 0) {
        if (!allowedRoles.includes(role)) {
          setStatus('forbidden');
          return;
        }
      }

      setStatus('authed');
    } catch (e) {
      // Clear any client-side auth markers/tokens.
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      } catch {}
      try {
        sessionStorage.clear();
      } catch {}

      if (lastCheckIdRef.current === checkId) setStatus('unauthed');
    }
  };

  useEffect(() => {
    checkAuth();

    // Handle BFCache restores + back/forward navigation.
    const onPageShow = () => {
      checkAuth();
    };

    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (status === 'checking') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'unauthed') {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  if (status === 'forbidden') {
    const target = forbiddenTo || getDefaultHomeForRole(userRole);
    return <Navigate to={target} replace />;
  }

  return children;
}
